import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllScheduleMasters, deleteScheduleMaster } from '../services/api';
import { toast } from 'react-toastify';
import './Masters.css';

const ScheduleMasters = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getAllScheduleMasters(page, 100);
      setSchedules(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scheduleId, scheduleName) => {
    if (window.confirm(`Are you sure you want to delete schedule "${scheduleName}"?`)) {
      try {
        await deleteScheduleMaster(scheduleId);
        toast.success('Schedule deleted successfully');
        fetchSchedules();
      } catch (error) {
        toast.error(error.message || 'Failed to delete schedule');
      }
    }
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      hourly: 'badge-info',
      daily: 'badge-success',
      weekly: 'badge-warning',
      monthly: 'badge-primary'
    };
    return colors[frequency] || 'badge-secondary';
  };

  if (loading) {
    return <div className="loading">Loading schedules...</div>;
  }

  return (
    <div className="masters-container">
      <div className="masters-header">
        <div>
          <h1>Schedule Masters</h1>
          <p className="subtitle">Manage reusable backup schedules</p>
        </div>
        <Link to="/schedules/new" className="btn btn-primary">
          + Create Schedule
        </Link>
      </div>

      {schedules.length === 0 ? (
        <div className="empty-state">
          <h3>No Schedules Found</h3>
          <p>Create your first schedule to get started</p>
          <Link to="/schedules/new" className="btn btn-primary">
            Create Schedule
          </Link>
        </div>
      ) : (
        <div className="masters-grid">
          {schedules.map((schedule) => (
            <div key={schedule._id} className="master-card">
              <div className="card-header">
                <h3>{schedule.name}</h3>
                <span className={`badge ${getFrequencyBadge(schedule.frequency)}`}>
                  {schedule.frequency}
                </span>
              </div>
              
              <div className="card-body">
                {schedule.description && (
                  <p className="description">{schedule.description}</p>
                )}
                
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Time:</span>
                    <span className="value">{schedule.time}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="label">Timezone:</span>
                    <span className="value">{schedule.timezone}</span>
                  </div>
                  
                  {schedule.frequency === 'weekly' && schedule.daysOfWeek?.length > 0 && (
                    <div className="info-item">
                      <span className="label">Days:</span>
                      <span className="value">
                        {schedule.daysOfWeek.map(day => 
                          ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                        ).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {schedule.frequency === 'monthly' && schedule.dayOfMonth && (
                    <div className="info-item">
                      <span className="label">Day of Month:</span>
                      <span className="value">{schedule.dayOfMonth}</span>
                    </div>
                  )}
                  
                  <div className="info-item">
                    <span className="label">Status:</span>
                    <span className={`badge ${schedule.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {schedule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="card-footer">
                <Link to={`/schedules/edit/${schedule._id}`} className="btn btn-sm btn-outline">
                  Edit
                </Link>
                <button
                  onClick={() => handleDelete(schedule._id, schedule.name)}
                  className="btn btn-sm btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduleMasters;